import { Stack } from '@hiiretail/react-foundation-ui';
import { useClickAway } from 'hooks/useClickAway';
import { ItemSearchReturnResponse } from 'app/services/itemsSearchService/itemsSearchTypes';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useAppDispatch } from 'app/store/store';
import { setSelectedItem } from 'features/itemSlice';
import { intl } from 'i18n/I18nProvider';
import { ItemsSearchInput } from './ItemsSearchInput';
import { ItemsSearchList } from './ItemsSearchList';
import { ItemsSearchResultItem } from './ItemsSearchResultItem';

interface ItemsSearchAutocompleteProps {
  value: string;
  onChangeText: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onClear: () => void;
  onSearch: () => void;
  items: ItemSearchReturnResponse[];
  loading: boolean;
  openSuggestions: boolean;
}

export const ItemsSearchAutocomplete: FC<ItemsSearchAutocompleteProps> = ({
  value,
  onChangeText,
  onSearch,
  onClear,
  items,
  loading,
  openSuggestions,
}) => {
  const [open, setOpen] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (openSuggestions) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [openSuggestions]);

  const autocompleteRef = useClickAway<HTMLDivElement>(() => {
    setOpen(false);
  });

  const handleInputFocus = () => {
    if (value && items) {
      setOpen(true);
    }
  };

  const handleSearchClick = () => {
    onSearch();
    setOpen(true);
  };

  const handleAddItem = (item: ItemSearchReturnResponse) => {
    const localedItem = {
      ...item,
      intlUnitOfMeasure: intl.formatMessage({id: 'item.input.unitOfMeasure.EA'}),
    }
    dispatch(setSelectedItem({ item: localedItem }));
    setOpen(false);
  };

  return (
    <Stack position="relative" width={350} ref={autocompleteRef}>
      <ItemsSearchInput
        value={value}
        onChangeText={onChangeText}
        onSearch={handleSearchClick}
        onClear={onClear}
        onFocus={handleInputFocus}
      />
      {open && (
        <ItemsSearchList
          data-testid="item-search-results"
          data={items}
          loading={loading}
          renderItem={(item) => (
            <ItemsSearchResultItem item={item} onAddItem={() => handleAddItem(item)} />
          )}
        />
      )}
    </Stack>
  );
};
